import { useMemo } from 'react';
import { useLeads } from '@/hooks/useLeads';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { Flame, Info } from 'lucide-react';
import { subDays, isAfter } from 'date-fns';

const TEMPERATURE_COLORS = {
  hot: 'hsl(0, 84%, 60%)',
  warm: 'hsl(38, 92%, 50%)',
  cold: 'hsl(210, 100%, 50%)',
};

interface TemperatureDistributionChartProps {
  timeRange: 7 | 30;
}

export function TemperatureDistributionChart({ timeRange }: TemperatureDistributionChartProps) {
  const { data: leads } = useLeads();

  const temperatureData = useMemo(() => {
    if (!leads) return [];

    const cutoffDate = subDays(new Date(), timeRange);
    
    // Filter leads created or updated within the time range
    const recentLeads = leads.filter(lead => {
      const createdAt = new Date(lead.created_at);
      const updatedAt = new Date(lead.updated_at);
      return isAfter(createdAt, cutoffDate) || isAfter(updatedAt, cutoffDate);
    });

    // Also filter out won/lost leads for active temperature tracking
    const activeLeads = recentLeads.filter(l => 
      l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost'
    );

    const hot = activeLeads.filter(l => l.temperature === 'hot').length;
    const warm = activeLeads.filter(l => l.temperature === 'warm').length;
    const cold = activeLeads.filter(l => l.temperature === 'cold').length;

    return [
      { name: 'Hot', value: hot, color: TEMPERATURE_COLORS.hot },
      { name: 'Warm', value: warm, color: TEMPERATURE_COLORS.warm },
      { name: 'Cold', value: cold, color: TEMPERATURE_COLORS.cold },
    ].filter(d => d.value > 0);
  }, [leads, timeRange]);

  const total = temperatureData.reduce((sum, d) => sum + d.value, 0);

  if (temperatureData.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-medium">Lead Temperature</CardTitle>
            <span className="text-xs text-muted-foreground">({timeRange}d)</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[160px] flex items-center justify-center text-muted-foreground text-sm">
            No active leads in period
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-medium">Lead Temperature</CardTitle>
            <span className="text-xs text-muted-foreground">({timeRange}d)</span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3.5 h-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[200px] text-xs">
              Distribution of active leads by temperature in the last {timeRange} days
            </TooltipContent>
          </Tooltip>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <div className="w-32 h-32">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={temperatureData}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={50}
                  dataKey="value"
                  nameKey="name"
                  strokeWidth={2}
                  stroke="hsl(var(--background))"
                >
                  {temperatureData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value: number, name: string) => [
                    `${value} leads (${((value / total) * 100).toFixed(0)}%)`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Legend */}
          <div className="flex-1 space-y-2">
            {temperatureData.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2.5 h-2.5 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm">{item.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium tabular-nums">{item.value}</span>
                  <span className="text-xs text-muted-foreground">
                    ({((item.value / total) * 100).toFixed(0)}%)
                  </span>
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-border/50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total Active</span>
                <span className="font-medium">{total}</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
